// Compatibility read model returned by /api/current_user. This is not a Tenant domain model.
import {
  type AppBusinessModel,
  type AppBusinessVertical,
} from '@bbk/contracts/business';
import { type RestFunnel } from '@bbk/contracts/funnels';
import { type PLANS } from '@bbk/contracts/plans';
import { type DateString } from '@bbk/contracts/type-utils';

export type RevenueSourceType = 'billing' | 'customer_field' | null;

export type RevenueSource = 'mrr' | 'price' | null;

export type ActiveApp = {
  show_demo_data?: boolean;
  show_play_demo_data?: boolean;
  get_started_status: 'COMPLETED' | 'ELIGIBLE' | 'INCOMPLETE';
  id: number;
  company_id: number;
  application_type: ApplicationType;
  funnel_selection_strategy: 'TREE' | 'RANDOM' | 'PERCENT_SPLIT';
  active: boolean;
  cb_product_catalog_version?: string;
  display_name: string;
  full_display_name: string;
  save_interval: number;
  cancel_unique_period: number;
  save_unique_period: number;
  user_session_interval: number;
  lco_accept_cooldown: string;
  lco_dismiss_cooldown: string;
  created_at: string;
  updated_at: string;
  launched_at: string;
  csp_domains: string[];
  cb_site_domain?: string;
  cb_site_id?: string | null;
  cb_site_region?: string;
  internal_name?: string;
  encoded_id: string;
  revenue_source: RevenueSource;
  revenue_source_type: RevenueSourceType;
  funnels: RestFunnel[];
  lifecycle_funnels?: RestFunnel[];
  first_non_test_session_date: string | null;
  api_auth_type: AppAuthType;
  business_type?: AppBusinessModel;
  industry_type?: AppBusinessVertical;
  company?: ActiveCompany;
  application_licenses: ApplicationLicense[];
  migrated_to_growth_v2_at?: string | null;
  vanity_domain_id?: string | null;
};

export enum PricingBookVersions {
  v1 = 'v1',
  v2 = 'v2',
}

export enum UserRoles {
  billing = 'billing',
}

export type ActiveCompany = {
  id: number;
  internal_name: string;
  name: string;
  company_type: CompanyType;
  domain: string;
  details?: {
    image_url?: string;
  };
  vanity_domain?: string;
  vanity_domain_active: boolean;
  plan?: PLANS;
  trial?: boolean;
  trial_end_date?: DateString;
  active: boolean;
  created_at: DateString;
  updated_at: DateString;
  first_payment_date?: DateString;
  churn_date?: DateString;
  owner_user_id: string;
  external_billing_id?: string;
  applications: ActiveApp[];
  price_book_version: PricingBookVersions;
  cb_merchant_id: string;
  multi_product_customer_id: string;
  user_roles: UserRoles[];
};

export type ActiveUser = {
  id: number;
  email: string;
  details: {
    image_url: string;
    language: string;
    display_name: string;
    first_name: string;
    last_name: string;
  };
  companies: ActiveCompany[];
  created_at: DateString;
  updated_at: DateString;
  authenticity_token: string;
  session_type?: 'cbidp' | 'dev';
  selected_company?: string;
  user_profile_url?: string;
  active: boolean;
  groups: string[];
  region?: string | null;
};

export enum AppAuthType {
  NONE = 'NONE',
  KEYPAIR_SIGNATURE = 'KEYPAIR_SIGNATURE',
}

export enum ProductKey {
  AGS = 'AGS',
  DEFLECT = 'DEFLECT',
  GROWTH_V2 = 'GROWTH_V2',
}

export type ProductLicense = {
  id: number;
  product_key: ProductKey;
  display_name: string;
};

export type ApplicationLicense = {
  active: boolean;
  id?: number;
  product_license: ProductLicense;
};

export enum ApplicationType {
  production = 'production',
  sandbox = 'sandbox',
}

export enum CompanyType {
  internal = 'internal',
  customer = 'customer',
  prospect = 'prospect',
  free = 'free',
}

// Existing Admin URL/HTTP identifier aliases, without new domain semantics.
export type AppKey = string;
export type CompanyId = number;
export type CompanyKey = string;
